import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import React, { Suspense, lazy } from 'react';
import styled from 'styled-components';
import BookingConfirmation from './components/BookingConfirmation';
import { Booking } from './types';
import { customClassNames } from './utils';

const loadModule = () => import('@calcom/atoms');

const Booker = lazy(() =>
    loadModule().then((module) => ({ default: module.Booker }))
);
const CalProvider = lazy(() =>
    loadModule().then((module) => ({ default: module.CalProvider }))
);

interface CalBookingProps {
    clientId: string;
    userName: string;
    eventSlug: string;
    localeCode?: any;
    apiUrl?: string;
    teamId: number;
}
export const API_URL = 'https://api.cal.dev/v2';
const DEFAULT_LOCALE = 'en';
const RuntimeCalBooking: RuntimeWidget = (props: CalBookingProps) => {
    const {
        localeCode = DEFAULT_LOCALE,
        apiUrl = API_URL,
        eventSlug,
        clientId,
        userName,
        teamId,
    } = props;
    const [confirmationData, setConfirmationData] =
        React.useState<Booking | null>(null);

    if (!eventSlug) {
        console.error('Booker - Event slug is required');
        return <div>Event slug is required</div>;
    }

    const clearConfirmationData = () => {
        setConfirmationData(null);
    };

    return (
        <CalBookingWrapper data-auto='ssr-cal-booking-widget'>
            <div className='booker-wrapper'>
                <Suspense fallback={<div>Loading Booker...</div>}>
                    <CalProvider
                        clientId={clientId}
                        options={{ apiUrl }}
                        language={localeCode}
                    >
                        {confirmationData && (
                            <BookingConfirmation
                                confirmationData={confirmationData}
                                clearConfirmationData={clearConfirmationData}
                            />
                        )}
                        <StyledBookerWrapper isHidden={!!confirmationData}>
                            <Booker
                                key={
                                    confirmationData
                                        ? 'booker-confirmed'
                                        : 'booker-initial'
                                }
                                eventSlug={eventSlug}
                                customClassNames={customClassNames}
                                onCreateBookingSuccess={(res) => {
                                    setConfirmationData(res.data);
                                }}
                                {...(teamId
                                    ? { isTeamEvent: true, teamId }
                                    : { username: userName })}
                            />
                        </StyledBookerWrapper>
                    </CalProvider>
                </Suspense>
            </div>
        </CalBookingWrapper>
    );
};
const StyledBookerWrapper = styled.div<{
    isHidden: boolean;
}>`
    display: ${(props) => (props.isHidden ? 'none' : 'block')};
`;

const CalBookingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    --booker-meta-width: 120px;
    .booker-wrapper {
        width: 100%;
        .calcom-atoms {
            .available-time-slots-header-container {
                display: flex;
                flex-wrap: wrap;
            }
            .booker-container {
                display: flex;
                box-sizing: border-box;
                > * {
                    width: auto;
                    flex: 1;
                }
                .event-meta-title {
                    font-size: x-large !important;
                }
            }
        }
    }
`;

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
